import React from 'react';
import { ShadedBox } from '../../Common/ShadedBox';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { Subtitle } from '../../Common/Subtitle';
import cx from 'classnames';
import { useResumeAddOn } from '../../../Hooks/useSubscription';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';

export interface AddOnCardProps {
  className?: string;
  descriptionClassName?: string;
  billingContainerClassName?: string;
  headerContainerClassName?: string;
  buttonContainerClassName?: string;
  imgUrl?: string;
  title?: string;
  description?: string;
  billingPrice?: string;
  billingCycle?: string;
  billingDescription?: string;
  ctaText?: string;
  onCancelClick?: () => void;
  onCtaClick?: () => void;
  onLearnMoreClick?: () => void;
  cancelled?: boolean;
  uuid?: string;
  cancelledText?: string;
}

export function AddOnCard({
  className,
  descriptionClassName,
  billingContainerClassName,
  headerContainerClassName,
  buttonContainerClassName,
  imgUrl,
  title,
  description,
  billingPrice,
  billingCycle,
  billingDescription,
  ctaText,
  onCancelClick,
  onCtaClick,
  onLearnMoreClick,
  cancelled,
  uuid,
  cancelledText,
}: AddOnCardProps) {
  const { onResumeClick: handleResumeClick, isResumingAddOn } =
    useResumeAddOn(uuid);

  return (
    <ShadedBox
      className={cx(
        className,
        'flex flex-col gap-y-3.5 px-4 py-3.5 h-full w-full lg:max-w-[384px]'
      )}
    >
      <div
        className={cx(
          headerContainerClassName,
          'flex flex-row flex-wrap gap-x-4 gap-y-4 items-center'
        )}
      >
        <img
          src={imgUrl}
          className='w-[60px] h-[60px] p-2 rounded border border-solid v2-border-neutral-300'
        />
        <Subtitle label={title} />
      </div>
      {description && (
        <span
          className={cx(
            descriptionClassName,
            'text-base v2-text-neutral-900 h-full'
          )}
        >
          {description}
        </span>
      )}

      {cancelled && cancelledText && (
        <span className={cx('text-base v2-text-neutral-900 h-full')}>
          {cancelledText}
        </span>
      )}

      <div
        className={cx(
          billingContainerClassName,
          'flex flex-row flex-wrap justify-between gap-y-4 items-end'
        )}
      >
        <div className='flex flex-row flex-wrap font-bold items-end v2-text-neutral-900'>
          <span className='text-[28px]'>{billingPrice}</span>
          <span className='text-[21px]'>{billingCycle}</span>
        </div>
        {billingDescription && !cancelled && (
          <span className='text-xs v2-text-neutral-900 leading-5'>
            {billingDescription}
          </span>
        )}
      </div>
      {(onCancelClick || onCtaClick || onLearnMoreClick) && (
        <div
          className={cx(
            buttonContainerClassName,
            'flex flex-col flex-wrap gap-4 font-bold min-h-[94px]'
          )}
        >
          {cancelled && (
            <Button
              variant={ButtonVariant.Primary}
              className='py-2 px-2.5'
              onClick={handleResumeClick}
            >
              Resume add-on{' '}
              <span>
                {isResumingAddOn && (
                  <FontAwesomeIcon className='spinning' icon={faSpinner} />
                )}
              </span>
            </Button>
          )}
          {onCancelClick && !cancelled && (
            <Button
              variant={ButtonVariant.DangerWhite}
              className='py-2 px-2.5'
              onClick={onCancelClick}
            >
              Cancel add-on
            </Button>
          )}

          {onCtaClick && !cancelled && (
            <Button
              variant={ButtonVariant.Primary}
              className='py-2 px-2.5'
              onClick={onCtaClick}
            >
              {ctaText}
            </Button>
          )}
          {onLearnMoreClick && (
            <Button
              variant={ButtonVariant.Secondary}
              className='py-2 px-2.5'
              onClick={onLearnMoreClick}
            >
              Learn more
            </Button>
          )}
        </div>
      )}
    </ShadedBox>
  );
}
